import { Form, FormControl, FormField, FormItem, FormMessage, FullCV, Input } from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { ArrayPath, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useFormArray } from '@/features/common/hooks/useFormArray';

import { useValidateOnMount } from '../../hooks/useValidateOnMount';
import { useVocalFormUpdate } from '../../hooks/useVocalFormUpdate';
import { createHobbiesSchema } from '../../schemas/hobbies.schema';
import FormLayout from './FormLayout';
import NumberedItem from './NumberedItem';

interface HobbiesAndInterestsFormProps {
  onSubmit: (data: FullCV) => void;
  initialData?: FullCV;
  onCancel: () => void;
}

const HobbiesAndInterestsForm: React.FC<HobbiesAndInterestsFormProps> = ({
  onSubmit,
  initialData,
  onCancel,
}) => {
  const { t } = useTranslation();

  const hobbiesSchema = createHobbiesSchema(t);
  type HobbiesFormValues = z.infer<typeof hobbiesSchema>;

  const form = useForm<HobbiesFormValues>({
    resolver: zodResolver(hobbiesSchema),
    defaultValues: initialData || {
      hobbiesAndInterests: [''],
    },
  });

  const { fields, items, canAdd, handleAdd, handleRemove } = useFormArray<HobbiesFormValues>({
    form,
    name: 'hobbiesAndInterests' as ArrayPath<HobbiesFormValues>,
    maxItems: 10,
  });

  const handleSubmit = (data: HobbiesFormValues) => {
    onSubmit(data as FullCV);
  };

  useValidateOnMount(form);
  useVocalFormUpdate(form, 'hobbiesAndInterests');

  return (
    <FormLayout
      title={t('Forms.HobbiesAndInterests.title')}
      onCancel={onCancel}
      onSubmit={form.handleSubmit(handleSubmit)}
      showAddButton={canAdd}
      onAdd={() => handleAdd('')}
      addButtonText={t('Forms.HobbiesAndInterests.addHobby')}
    >
      <Form {...form}>
        <form className="space-y-6 pb-20">
          {fields.map((field, index) => (
            <NumberedItem
              key={field.id}
              index={index}
              showDelete={items.length > 1}
              className="items-center"
              onDelete={() => handleRemove(index)}
            >
              <FormField
                control={form.control}
                name={`hobbiesAndInterests.${index}`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="h-12 w-full min-w-0"
                        placeholder={t('Forms.HobbiesAndInterests.hobbyPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </NumberedItem>
          ))}
        </form>
      </Form>
    </FormLayout>
  );
};

export default HobbiesAndInterestsForm;
